import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { can as canFn, type Me } from '@hrms/shared';
import { api, onUnauthorized } from '../lib/api';
import { deviceName } from '../lib/platform';
import { registerForPush } from '../lib/push';
import { getToken, KEYS, setToken, storage } from '../lib/storage';

interface AuthValue {
  me: Me | null;
  /** true while the stored token is being validated with /auth/me */
  loading: boolean;
  isAuthenticated: boolean;
  login: (b: { tenant_code: string; email: string; password: string }) => Promise<Me>;
  logout: () => Promise<void>;
  /** true if the user has ANY of the given permissions (keys from `P` in @hrms/shared). */
  can: (...keys: string[]) => boolean;
}

const AuthCtx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const [token, setTok] = useState<string | null>(() => getToken());

  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: () => api.auth.me(),
    enabled: !!token,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const clear = useCallback(() => {
    void setToken(null);
    setTok(null);
    qc.clear();
  }, [qc]);

  useEffect(() => onUnauthorized(clear), [clear]);

  useEffect(() => {
    if (meQ.data) void registerForPush(api);
  }, [meQ.data]);

  const login = useCallback<AuthValue['login']>(
    async (b) => {
      const res = await api.auth.login({ ...b, device_name: deviceName });
      await setToken(res.token);
      void storage.set(KEYS.lastTenant, b.tenant_code);
      void storage.set(KEYS.lastEmail, b.email);
      qc.setQueryData(['me'], res.user);
      setTok(res.token);
      return res.user;
    },
    [qc],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      /* token may already be invalid */
    }
    clear();
  }, [clear]);

  const me = token ? meQ.data ?? null : null;
  const value = useMemo<AuthValue>(
    () => ({
      me,
      loading: !!token && meQ.isLoading,
      isAuthenticated: !!token && !!me,
      login,
      logout,
      can: (...keys: string[]) => canFn(me?.permissions, ...keys),
    }),
    [me, token, meQ.isLoading, login, logout],
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const c = useContext(AuthCtx);
  if (!c) throw new Error('AuthProvider missing');
  return c;
}
